/**
 * Price Histogram Query Hook
 *
 * Feeds the price filter's rent-distribution chart. The chart is a coarse
 * orientation aid, so it is scoped to the searched area rather than refetched
 * on every map nudge — the live viewport is used only once the user has panned
 * the map for the area they are searching.
 */

import { useMemo } from 'react';
import { useQuery } from '@zg-rentals/react-query5';
import {
  fetchPriceHistogram,
  type PriceHistogram,
  type PriceHistogramParams,
} from '@/src/lib/api/endpoints/priceHistogram';
import type { AreaResponse, AutocompleteResult } from '@/src/lib/api/endpoints/area';
import { toApiParams } from '@/src/lib/filters';
import type { FilterState } from '@/src/lib/filters/filterTypes';
import { useMapPosition } from '@/src/lib/hooks/map';
import { useCurrentArea } from './useCurrentArea';

export const priceHistogramKeys = {
  all: ['priceHistogram'] as const,
  byParams: (params: PriceHistogramParams) => [...priceHistogramKeys.all, params] as const,
};

type BoundingBoxParams = Pick<PriceHistogramParams, 'minLat' | 'maxLat' | 'minLon' | 'maxLon'>;

function getAreaBoundingBox(area: AreaResponse | AutocompleteResult | null | undefined): BoundingBoxParams | null {
  if (!area || !('minLat' in area)) return null;
  const { minLat, maxLat, minLon, maxLon } = area;
  if (minLat === undefined || maxLat === undefined || minLon === undefined || maxLon === undefined) return null;
  return { minLat, maxLat, minLon, maxLon };
}

type UsePriceHistogramArgs = {
  /** Sibling filters — every one but price narrows the distribution. */
  filters: FilterState;
  /** Area being searched; autocomplete results carry no bounds and fall through. */
  area?: AreaResponse | AutocompleteResult | null;
  /** Gate the request on the filter surface actually being open. */
  enabled?: boolean;
};

export function usePriceHistogram({ filters, area, enabled = true }: UsePriceHistogramArgs) {
  const { data: currentArea } = useCurrentArea();
  const mapPosition = useMapPosition();

  // A caller that names an area owns the scope. If that area carries no bounds
  // — an autocomplete pick stages a bare id in navigate mode, and stays that
  // way until Search resolves it — chart nothing, rather than falling through
  // to `currentArea` and drawing the *previously* searched city's rents under
  // the location the user just picked. Only a caller that passes no area at all
  // (the shell-hosted modal, whose scope really is the current area) falls back.
  const scopeArea = area === undefined ? currentArea : getAreaBoundingBox(area) ? (area as AreaResponse) : null;

  const params = useMemo<PriceHistogramParams | null>(() => {
    // The cached map position outlives the SRP it was set on, so it only counts
    // when it belongs to the area being searched.
    const boundingBox =
      mapPosition && scopeArea?.id && mapPosition.areaKey === scopeArea.id
        ? {
            minLat: mapPosition.bounds.southLat,
            maxLat: mapPosition.bounds.northLat,
            minLon: mapPosition.bounds.westLng,
            maxLon: mapPosition.bounds.eastLng,
          }
        : getAreaBoundingBox(scopeArea);

    if (!boundingBox) return null;

    // Price bounds come off: the chart draws the full range and grays out what
    // the price filter excludes. `lowPrice`/`highPrice` are the V2 price params.
    const { lowPrice: _lowPrice, highPrice: _highPrice, ...filterParams } = toApiParams(filters);

    const next: PriceHistogramParams = { ...filterParams, ...boundingBox };

    // University area ids are unsearchable in the model index (HPWEB-5750).
    if (scopeArea?.id && scopeArea.type !== 'university') {
      next.areas = scopeArea.id;
    }

    return next;
  }, [filters, mapPosition, scopeArea]);

  return useQuery<PriceHistogram>({
    queryKey: params ? priceHistogramKeys.byParams(params) : [...priceHistogramKeys.all, 'disabled'],
    queryFn: ({ signal }) => fetchPriceHistogram(params!, signal),
    enabled: enabled && !!params,
    staleTime: 5 * 60 * 1000,
    gcTime: typeof window === 'undefined' ? Infinity : 10 * 60 * 1000,
    // Keep the previous area's bars on screen while the next request lands
    // instead of collapsing the dropdown's height mid-interaction.
    placeholderData: (previous) => previous,
    meta: {
      tags: ['price-histogram'],
      context: { api: 'hotpads-api', details: 'GET /v2/listing/priceHistogram for the price filter chart' },
    },
  });
}
